import { type Dispatch, type SetStateAction, useState } from 'react'

import { Alert, Checkbox, Modal, Multiselect, Select } from 'components'
import { CriticismType, YesNoNotApplicable } from 'constants/enums'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { approveMaterials, rejectMaterials } from 'services/analysis'
import type {
  MaterialItensAction,
  TipoCriticaVolume,
} from 'store/ExtraVolumeContext/Types/ExtraVolumeType'
import { useSelector } from 'store/store'

import * as S from './styles'

interface ModalConfirmationProps {
  isOpen: boolean
  criticType: TipoCriticaVolume
  isReject: boolean
  itens: MaterialItensAction[]
  setIsOpen: Dispatch<SetStateAction<boolean>>
  handleContinue: () => void
  handleReturnAction: () => void
}

export const ModalConfirmation = ({
  isOpen = false,
  itens,
  criticType,
  setIsOpen,
  isReject,
  handleContinue,
  handleReturnAction,
}: ModalConfirmationProps) => {
  const defaultValues = { cd_moti_repv_anls: '', restrito: false }
  const methods = useForm({ defaultValues })
  const { control, getValues, watch } = methods

  const [loading, setLoading] = useState(false)

  function getColors(i: MaterialItensAction[]) {
    const colorOptions = [...new Set(i.flatMap((i) => i?.cores))]
    return colorOptions as string[]
  }

  const [colors, setColors] = useState(getColors(itens))

  const { rejectionMotives } = useSelector((store) => store.extraVolume)
  const motiveList = rejectionMotives?.map((r) => ({
    title: r.ds_moti_repv_anls,
    value: r.cd_moti_repv_anls,
  }))

  const handleConfirm = async () => {
    setLoading(true)

    try {
      if (isReject) {
        const { cd_moti_repv_anls, restrito } = getValues()
        const reject = await rejectMaterials({
          cd_moti_repv_anls,
          itens,
          restrito: restrito ? YesNoNotApplicable.YES_UPPERCASE : YesNoNotApplicable.NO_UPPERCASE,
        })

        if (reject?.return_code === 0) {
          handleContinue()
        } else {
          toast.error(reject?.return_chav)
        }
      } else {
        const newItens = itens.map((i) => {
          if (i.cores) {
            return { ...i, cores: i?.cores?.filter((c) => colors?.includes(c)) }
          } else {
            return i
          }
        })

        const approved = await approveMaterials(newItens)

        if (approved?.return_code === 0) {
          handleContinue()
        } else {
          toast.error(approved?.return_chav)
        }
      }
    } catch {
      toast.error(
        'Algo deu errado! Tente novamente mais tarde ou entre em contato com o time de Backoffice.',
      )
    } finally {
      setLoading(false)
    }
  }

  const onChange = (e: string[]) => {
    setColors(e)
  }

  const disableButton = isReject
    ? !watch('cd_moti_repv_anls')
    : criticType !== CriticismType.QUOTA && !colors.length

  return (
    <Modal
      buttonAction={handleConfirm}
      buttonDisabled={disableButton}
      buttonText={isReject ? 'Reprovar' : 'Aprovar'}
      loading={loading}
      open={isOpen}
      returnAction={handleReturnAction}
      returnText="Voltar"
      setOpen={setIsOpen}
      showClose={false}
      showReturn
      title={isReject ? 'Reprovação' : 'Aprovação'}
    >
      <S.Wrapper>
        {isReject && (
          <>
            <S.Description>
              O material ficará em aberto no pedido, permitindo que o executivo aguarde a abertura
              de novos níveis de cota ou modifique o volume anteriormente solicitado.
              <br />
              <br />
              Caso marque a opção de restringir novas tentativas, o material irá para a aba de saldo
              e só poderá solicitar um volume extra para o mesmo cliente em um mês cota posterior.
            </S.Description>
            <S.FieldWrapper>
              <Controller
                control={control}
                name="cd_moti_repv_anls"
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    disabled={loading}
                    label="Motivo ou instrução"
                    name={name}
                    onChange={onChange}
                    options={motiveList}
                    value={value}
                  />
                )}
              />
            </S.FieldWrapper>
            <Controller
              control={control}
              defaultValue={false}
              name="restrito"
              render={({ field: { name, value, onChange } }) => (
                <S.WrapCheckBox>
                  <Checkbox
                    checked={value}
                    disabled={loading}
                    id={name}
                    label="Restringir tentativas deste cliente neste mês cota"
                    onChange={(e) => {
                      onChange(e.target.checked)
                    }}
                  />
                </S.WrapCheckBox>
              )}
            />
          </>
        )}
        {!isReject && (
          <>
            <S.Description>
              A liberação do volume extra será autorizada, e assim que a análise de todos os
              materiais de um pedido for concluída, o executivo será notificado e poderá dar
              continuidade à venda.
            </S.Description>
            {criticType !== CriticismType.QUOTA && (
              <>
                <Multiselect
                  disabled={loading}
                  label="Selecione as cores que deseja aprovar"
                  onChange={onChange}
                  optionsList={getColors(itens) as string[]}
                  selectedOptions={colors as string[]}
                  width={450}
                />
                <S.AlertBox>
                  <Alert
                    fullWidth
                    kind="warning"
                    message={
                      <S.AlertContent>
                        <li>
                          A aprovação das solicitações de volume extra gera novas demandas para o
                          plano de produção.
                        </li>
                        <li>
                          Mesmo que uma cor tenha estoque disponível, a aprovação poderá gerar plano
                          de produção para <strong>todos os materiais</strong> dessa cor.
                        </li>
                      </S.AlertContent>
                    }
                    title="Atenção"
                  />
                </S.AlertBox>
              </>
            )}
          </>
        )}
      </S.Wrapper>
    </Modal>
  )
}
